/**
 * thread.ts — data helpers for The Thread (daily check-in) and
 * Your Pattern (Awakening Timeline synthesis).
 */
import { db, supabase } from './supabase'

export interface ThreadEntry {
  id: string
  user_id: string
  entry_date: string          // ISO date string 'YYYY-MM-DD'
  responses: ThreadResponses
  daily_digest: string | null
  crisis_flag: boolean
  created_at: string
  updated_at: string
}

export interface ThreadResponses {
  emotion?: string            // Q1
  trigger?: string            // Q2
  familiar?: string           // Q3
  intention?: string          // optional
  gratitude?: string          // optional
  release?: string            // optional
}

export interface PatternNarrative {
  emotions: string
  pattern: string
  gratitude: string | null
  quote: string
  question: string
}

export interface AwakeningTimeline {
  id: string
  user_id: string
  period_days: 30 | 60 | 90
  period_key: string
  narrative: PatternNarrative
  entry_count: number
  generated_at: string
}

/** Today's date as 'YYYY-MM-DD' in local time. */
export function todayDate(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** Upsert today's thread entry (one per user per day). Returns the saved row. */
export async function saveThreadEntry(
  userId: string,
  responses: ThreadResponses,
  crisisFlag = false,
): Promise<ThreadEntry | null> {
  const { data, error } = await db()
    .from('thread_entries')
    .upsert(
      {
        user_id: userId,
        entry_date: todayDate(),
        responses,
        crisis_flag: crisisFlag,
      },
      { onConflict: 'user_id,entry_date' },
    )
    .select()
    .single()
  if (error) { console.error('saveThreadEntry:', error); return null }
  return data as ThreadEntry
}

/** Fetch today's entry, or null if none exists. */
export async function fetchTodayEntry(userId: string): Promise<ThreadEntry | null> {
  const { data } = await db()
    .from('thread_entries')
    .select('*')
    .eq('user_id', userId)
    .eq('entry_date', todayDate())
    .maybeSingle()
  return (data as ThreadEntry | null) ?? null
}

/** Fetch the N most recent entries (for calendar / count display). */
export async function fetchRecentEntries(userId: string, limit = 90): Promise<ThreadEntry[]> {
  const { data } = await db()
    .from('thread_entries')
    .select('id, entry_date, responses, daily_digest, crisis_flag, created_at')
    .eq('user_id', userId)
    .order('entry_date', { ascending: false })
    .limit(limit)
  return (data ?? []) as ThreadEntry[]
}

/** Total check-in count for a user (used for "7+ entries" Pattern gate). */
export async function fetchEntryCount(userId: string): Promise<number> {
  const { count } = await db()
    .from('thread_entries')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .not('responses', 'eq', '{}')  // only count entries that have at least one response
  return count ?? 0
}

/** Fetch a cached timeline for a given period. */
export async function fetchTimeline(
  userId: string,
  periodDays: 30 | 60 | 90,
): Promise<AwakeningTimeline | null> {
  const { data } = await db()
    .from('awakening_timelines')
    .select('*')
    .eq('user_id', userId)
    .eq('period_days', periodDays)
    .order('generated_at', { ascending: false })
    .limit(1)
    .maybeSingle()
  return (data as AwakeningTimeline | null) ?? null
}

/** Call the generate-pattern Edge Function. Returns the timeline or null. */
export async function generatePattern(
  periodDays: 30 | 60 | 90,
): Promise<AwakeningTimeline | null> {
  if (!supabase) return null
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) return null

  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-pattern`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ period_days: periodDays }),
    },
  )
  if (!res.ok) return null
  return res.json() as Promise<AwakeningTimeline>
}

/** Trigger async digest generation after a save (fire-and-forget). */
export async function triggerDigest(entryId: string): Promise<void> {
  if (!supabase) return
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) return
  fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-thread-digest`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ entry_id: entryId }),
    },
  ).catch(() => {/* fire-and-forget — digest failure is non-blocking */})
}

// Re-export from shared crisis lib so callers only need to import from thread.ts
export { detectCrisis } from './crisis'
